import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Data shape required for profile completion calculation.
 * Works with both CandidateViewModel and form data.
 */
export interface ProfileCompletionData {
    firstName: string;
    lastName: string;
    mobilePhone: string | null;
    location: unknown; // Can be LocationModel, LocationKey, or null
    roles: unknown[]; // flat roles or role/speciality pairs — only length is used here
    skills: Array<{ name: string }>;
}

interface ProfileCompletionField {
    key: string;
    weight: number;
    isComplete: boolean;
    tipKey: string;
}

export interface ProfileCompletionResult {
    percentage: number;
    tip: string | null;
    isComplete: boolean;
}

export function useProfileCompletion(data: ProfileCompletionData | null): ProfileCompletionResult {
    const { t } = useTranslation('profile');

    return useMemo(() => {
        if (!data) {
            return { percentage: 0, tip: null, isComplete: false };
        }

        const fields: ProfileCompletionField[] = [
            {
                key: 'firstName',
                weight: 15,
                isComplete: Boolean(data.firstName?.trim()),
                tipKey: 'completion.tips.firstName'
            },
            {
                key: 'lastName',
                weight: 15,
                isComplete: Boolean(data.lastName?.trim()),
                tipKey: 'completion.tips.lastName'
            },
            {
                key: 'mobilePhone',
                weight: 15,
                isComplete: Boolean(data.mobilePhone?.trim()),
                tipKey: 'completion.tips.mobilePhone'
            },
            {
                key: 'location',
                weight: 15,
                isComplete: data.location !== null,
                tipKey: 'completion.tips.location'
            },
            {
                key: 'roles',
                weight: 20,
                isComplete: data.roles.length > 0,
                tipKey: 'completion.tips.roles'
            },
            {
                key: 'skills',
                weight: 20,
                isComplete: data.skills.length > 0,
                tipKey: 'completion.tips.skills'
            }
        ];

        const completedWeight = fields
            .filter(field => field.isComplete)
            .reduce((sum, field) => sum + field.weight, 0);

        const percentage = Math.round(completedWeight);

        // Find the first incomplete field to show as a tip (prioritized by importance)
        const priorityOrder = [ 'roles', 'skills', 'location', 'mobilePhone', 'firstName', 'lastName' ];
        const incompleteField = priorityOrder
            .map(key => fields.find(f => f.key === key))
            .find(field => field && !field.isComplete);

        const tip = incompleteField ? t(incompleteField.tipKey) : null;

        return {
            percentage,
            tip,
            isComplete: percentage === 100
        };
    }, [ data, t ]);
}
